import { app } from '../App';
import { StringExtras } from '../utils/StringExtras';

export const CopyFormat = {
  CopyLang: 'copyLang',
  LangCopy: 'langCopy'
} as const;

export type CopyFormatKeys = keyof typeof CopyFormat;
export type CopyFormatValues = typeof CopyFormat[CopyFormatKeys];

export interface LanguageInfo {
  rtl?: string[];
}

export interface LocalisationData {
  languages: string[];
  languageInfo?: LanguageInfo;
  languageNames?: { [key: string]: string };
  resources?: LocalisationResources;
  copyFormat: CopyFormatValues;
  copy?: { [key: string]: { [key: string]: string } };
}

export interface LocalisationResources {
  images?: { [imageId: string]: LocalisationImage };
  fonts?: LocalisationFonts;
}

export interface LocalisationImage {
  texture: string | { [language: string]: string };
  frame: string | { [language: string]: string };
}

export interface LocalisationFonts {
  // Allow grouping languages under single id to reduce duplication.
  // groups?: { [groupName: string]: string[] };

  // Currently defaults set in phaserEditor. Could allow global defaults to be set.
  // defaultFontFamilies?: string;
  // defaults?: { [key: string]: { [key: string]: string } };

  settings?: { [language: string]: { [font: string]: FontSettings } };
  fallback?: { [key: string]: { [key: string]: string } };
}

export interface FontSettings {
  padding?: Phaser.Types.GameObjects.Text.TextPadding;
}

export interface LocaleImage {
  texture: string;
  frame: string;
}

export class LocaleHandler {
  public static events = {
    LanguageChanged: 'languageChanged'
  } as const;

  private localisationData: LocalisationData | undefined;
  private defaultLanguage: string;
  private selectedLanguage: string;

  constructor() {
    this.defaultLanguage = 'en';
    this.selectedLanguage = this.defaultLanguage;
  }

  get SelectedLanguage() {
    return this.selectedLanguage;
  }

  public init(localisationData: LocalisationData, selectedLanguage: string) {
    this.localisationData = localisationData;

    if (this.localisationData.languages.includes(selectedLanguage)) this.selectedLanguage = selectedLanguage;
    else this.selectedLanguage = this.defaultLanguage;
  }

  public changeLanguage(scene: Phaser.Scene, newLanguage: string) {
    if (this?.localisationData?.languages.includes(newLanguage)) this.selectedLanguage = newLanguage;
    else this.selectedLanguage = this.defaultLanguage;

    this.refreshAllText(scene, newLanguage, this.localisationData?.languageInfo ?? {});
  }

  public refreshAllText(scene: Phaser.Scene, newLanguage: string, languageInfo: LanguageInfo) {
    scene.events.emit(LocaleHandler.events.LanguageChanged, newLanguage, languageInfo);
  }

  public getLanguageInfo() {
    return this.localisationData?.languageInfo ?? {};
  }

  public getFont(defaultFont: string): string {
    // Sometimes the font has quotes on it.
    let trimmedFont = StringExtras.removeMatchingWrappingCharacters('"', defaultFont);

    if (
      this.localisationData?.resources?.fonts?.fallback &&
      this.localisationData?.resources?.fonts?.fallback[this.SelectedLanguage] &&
      this.localisationData?.resources?.fonts?.fallback[this.SelectedLanguage][trimmedFont]
    ) {
      return this.localisationData.resources.fonts.fallback[this.SelectedLanguage][trimmedFont];
    }

    return defaultFont;
  }

  public getFontSettings(font: string): FontSettings {
    // Sometimes the font has quotes on it.
    let trimmedFont = StringExtras.removeMatchingWrappingCharacters('"', font);

    if (
      this?.localisationData?.resources?.fonts?.settings &&
      this?.localisationData?.resources?.fonts?.settings[this.SelectedLanguage] &&
      this?.localisationData?.resources?.fonts?.settings[this.SelectedLanguage][trimmedFont]
    ) {
      return this.localisationData.resources.fonts.settings[this.SelectedLanguage][trimmedFont];
    }

    return {};
  }

  public getLocalisedTextById(copyId: string, options: { useRTLFix?: boolean } = {}): string {
    if (!this?.localisationData?.copyFormat) return '';

    let copy = '';
    if (this.localisationData.copyFormat == CopyFormat.CopyLang) {
      copy = this.getLocalisedText(copyId, this.selectedLanguage);
    } else if (this.localisationData.copyFormat == CopyFormat.LangCopy) {
      copy = this.getLocalisedText(this.selectedLanguage, copyId);
    }

    if (options.useRTLFix) copy = '‏' + copy + '‏';

    return copy;
  }

  public getLocalisedImageFromId(imageId: string, defaultImage: LocaleImage): LocaleImage {
    if (!this?.localisationData?.resources?.images || !imageId || !this.localisationData.resources.images[imageId]) {
      return defaultImage;
    } else {
      let image = { texture: '', frame: '' };
      let localImage = this.localisationData.resources.images[imageId];

      let foundTexture = false;
      let foundFrame = false;

      if (typeof localImage.texture === 'string') {
        image.texture = localImage.texture;
        foundTexture = true;
      } else if (typeof localImage.texture === 'object' && typeof localImage.texture[this.selectedLanguage] === 'string') {
        image.texture = localImage.texture[this.selectedLanguage];
        foundTexture = true;
      }

      if (typeof localImage.frame === 'string') {
        image.frame = localImage.frame;
        foundFrame = true;
      } else if (typeof localImage.frame === 'object' && typeof localImage.frame[this.selectedLanguage] === 'string') {
        image.frame = localImage.frame[this.selectedLanguage];
        foundFrame = true;
      }

      return foundTexture && foundFrame ? image : defaultImage;
    }
  }

  protected getLocalisedText(firstId: string, secondId: string): string {
    if (!this?.localisationData?.copy || !firstId || !this.localisationData.copy[firstId] || !this.localisationData.copy[firstId][secondId]) return '';
    else return this.localisationData.copy[firstId][secondId];
  }
}
